import type { FC } from 'react';
import clsx from 'clsx';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card/Card';
import { useTranslation } from '@/hooks/useTranslation';
import { formatNumber, formatProbability, formatWear, plural, wearLevel } from '../lib/wearFormat';
import type { WearOutput } from '../type/WearTypes';

/** The gauge runs to 120 % of the predicted life so a worn tool still shows its overshoot. */
const GAUGE_MAX = 1.2;

const LEVEL_FILL = {
  good: 'bg-success',
  warn: 'bg-warning',
  bad: 'bg-destructive',
} as const;

/** Wear in % of the predicted life, probability of being worn, pieces milled, gauge. */
export const WearStateCard: FC<{ output: WearOutput }> = ({ output }) => {
  const { t, language } = useTranslation();
  const level = wearLevel(output.wear, output.prudentWear);
  const at = (value: number) => `${(Math.min(value, GAUGE_MAX) / GAUGE_MAX) * 100}%`;
  const milled = output.traces.reduce((sum, trace) => sum + (trace.pieces ?? 0), 0);
  // Traces left out of the wear (no ae, fz or volume): the values below are minimums.
  const lowerBound = output.skipped > 0 && (
    <Badge tone="warning" size="sm" data-testid="wear-lower-bound">
      {t('mlWear.result.lowerBoundTag')}
    </Badge>
  );

  return (
    <Card as="section" className="flex flex-col gap-4" data-testid="wear-state">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold">{t('mlWear.result.stateTitle')}</h2>
        <Badge>
          <span className={clsx('h-2 w-2 rounded-full', LEVEL_FILL[level])} aria-hidden />
          {plural(t, 'mlWear.result.tracesCounted', output.counted)}
          {output.skipped > 0 && ` · ${plural(t, 'mlWear.result.tracesSkipped', output.skipped)}`}
        </Badge>
      </div>
      <div className="flex flex-wrap items-end gap-x-8 gap-y-4">
        <div className="flex flex-col gap-0.5">
          <span className="text-xs text-muted-foreground">{t('mlWear.result.wear')}</span>
          <span className="flex items-baseline gap-1.5">
            <span
              className="text-5xl font-extrabold leading-none tracking-tight tabular-nums"
              data-testid="wear-value"
            >
              {formatWear(language, output.wear)}
            </span>
            <span className="text-sm text-muted-foreground">{t('mlWear.result.wearOfLife')}</span>
            {lowerBound}
          </span>
        </div>
        <div className="flex flex-col gap-0.5">
          <span className="text-xs text-muted-foreground">{t('mlWear.result.pWorn')}</span>
          <span className="flex items-baseline gap-1.5">
            <span className="text-xl font-bold tabular-nums" data-testid="wear-p-worn">
              {formatProbability(language, output.pWorn)}
            </span>
            {lowerBound}
          </span>
        </div>
        <div className="flex flex-col gap-0.5">
          <span className="text-xs text-muted-foreground">{t('mlWear.result.milled')}</span>
          <span className="text-xl font-bold tabular-nums">
            {plural(t, 'mlWear.result.milledPieces', milled)}
          </span>
        </div>
      </div>
      <div
        className="relative pb-6 pt-1"
        role="img"
        aria-label={t('mlWear.result.gaugeLabel', { wear: formatWear(language, output.wear) })}
      >
        <div className="relative h-3 overflow-hidden rounded-full border border-border bg-muted">
          <div
            className={clsx('absolute inset-y-0 left-0 rounded-full', LEVEL_FILL[level])}
            style={{ width: at(output.wear) }}
          />
        </div>
        {[
          {
            value: output.prudentWear,
            label: t('mlWear.result.gaugePrudent', {
              value: `${formatNumber(language, output.prudentWear * 100, 0)} %`,
            }),
          },
          { value: 1, label: t('mlWear.result.gaugeEnd') },
        ].map((mark) => (
          <span key={mark.label}>
            <span
              className="absolute top-0 h-[18px] w-0.5 -translate-x-1/2 bg-foreground/60"
              style={{ left: at(mark.value) }}
            />
            <span
              className="absolute top-6 -translate-x-1/2 whitespace-nowrap text-2xs text-muted-foreground"
              style={{ left: at(mark.value) }}
            >
              {mark.label}
            </span>
          </span>
        ))}
      </div>
    </Card>
  );
};
